/**
 * Frontend token counter service for context window management
 * Uses tiktoken library for accurate token counting
 */

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

class TokenCounterService {
  private readonly CONTEXT_WINDOW_SIZE = 128000; // GPT-4o-mini context window
  private readonly THRESHOLD_PERCENTAGE = 0.7; // 70%
  private encoder: any = null;
  private initialized = false;

  /**
   * Initialize the token counter
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Dynamic import to handle optional dependency
      const { encoding_for_model } = await import('tiktoken');
      this.encoder = encoding_for_model('gpt-4o-mini');
      this.initialized = true;
      console.log('[TokenCounter] Initialized with tiktoken');
    } catch (error) {
      console.warn(
        '[TokenCounter] tiktoken not available, using fallback estimation',
        error
      );
      this.encoder = null;
      this.initialized = true;
    }
  }

  /**
   * Count tokens in a single message
   */
  countMessageTokens(message: Message): number {
    if (!message.content) return 0;

    if (this.encoder) {
      try {
        return this.encoder.encode(message.content).length;
      } catch (error) {
        console.warn('[TokenCounter] Error encoding message, using fallback');
      }
    }

    // Fallback: rough estimation (1 token ≈ 4 characters)
    return Math.ceil(message.content.length / 4);
  }

  /**
   * Count tokens in an array of messages
   */
  countTokens(messages: Message[]): number {
    let totalTokens = 0;

    // Add system message overhead (approximately 10 tokens)
    totalTokens += 10;

    for (const message of messages) {
      // Add role overhead (approximately 3 tokens per message)
      totalTokens += 3;
      totalTokens += this.countMessageTokens(message);
    }

    return totalTokens;
  }

  /**
   * Get the context window threshold (70% of total)
   */
  getThreshold(): number {
    return Math.floor(this.CONTEXT_WINDOW_SIZE * this.THRESHOLD_PERCENTAGE);
  }

  /**
   * Check if summarization should be triggered
   */
  shouldSummarize(currentCount: number): boolean {
    return currentCount >= this.getThreshold();
  }

  /**
   * Get context window size
   */
  getContextWindowSize(): number {
    return this.CONTEXT_WINDOW_SIZE;
  }

  /**
   * Get threshold percentage
   */
  getThresholdPercentage(): number {
    return this.THRESHOLD_PERCENTAGE;
  }
}

export const tokenCounterService = new TokenCounterService();
